import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';

import { EnvironmentConfig } from '../config/environment.config';
import { AiQASearch, AiQASearchHistory } from '../models/ai-qa-history.model';
import { AiTranscriptDigest } from '../models/ai-transcript.model';

@Injectable({
  providedIn: 'root'
})
export class GenieService {
  constructor(
    @Inject('environment') private environment: EnvironmentConfig,
    private http: HttpClient
  ) {}

  getAiQASearchHistory(projectId: string, methodId: string): Observable<AiQASearchHistory> {
    return this.http.get<AiQASearchHistory>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/survey/analysis/question`).pipe(
      map((result) => {
        result.searches = this.formatSearchesReferences(result.searches);
        return result;
      }),
      catchError(() => of(null))
    );
  }

  getAiSummary(projectId: string, methodId: string): Observable<AiTranscriptDigest> {
    return this.http.get<AiTranscriptDigest>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/survey/analysis/overview`).pipe(
      map((transcript) => {
        const formattedTranscript = transcript;
        formattedTranscript.themes?.forEach((theme) => {
          theme.body = this.formatReference(theme.body);
        });

        formattedTranscript.findings?.forEach((finding) => {
          finding.body = this.formatReference(finding.body);
        });

        formattedTranscript.opportunities?.forEach((opportunity) => {
          opportunity.body = this.formatReference(opportunity.body);
        });

        return formattedTranscript;
      }),
      catchError(() => of(null))
    );
  }

  sendAiQAQuestion(projectId: string, methodId: string, text: string, repliedToId: string = null): Observable<AiQASearch> {
    return this.http
      .post<AiQASearch>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/survey/analysis/question`, { text, repliedToId })
      .pipe(
        map((result) => {
          result.answer = this.formatReference(result.answer);

          return result;
        }),
        catchError(() => of(null))
      );
  }

  runAIAnalysis(projectId: string, methodId: string): Observable<boolean> {
    const url = `${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/survey/analysis/overview`;
    return this.http.post<boolean>(url, {}).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getDiscussionTopicAiSummary(projectId: string, methodId: string, topicId: string): Observable<AiTranscriptDigest> {
    return this.http
      .get<AiTranscriptDigest>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/discussion-forum/${topicId}/analysis/overview`)
      .pipe(
        map((summary) => {
          summary.findings?.forEach((item) => {
            item.body = this.formatReference(item.body);
          });
          summary.themes?.forEach((item) => {
            item.body = this.formatReference(item.body);
          });
          summary.opportunities?.forEach((item) => {
            item.body = this.formatReference(item.body);
          });
          summary.questionSummaries?.forEach((item) => {
            item.body = this.formatReference(item.body);
          });
          return summary;
        }),
        catchError(() => of(null))
      );
  }

  runDiscussionTopicAIAnalysis(projectId: string, methodId: string, topicId: string): Observable<boolean> {
    const url = `${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/discussion-forum/${topicId}/analysis/overview`;
    return this.http.post<boolean>(url, {}).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  getDiscussionTopicAiQASearchHistory(projectId: string, methodId: string, topicId: string): Observable<AiQASearchHistory> {
    return this.http
      .get<AiQASearchHistory>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/discussion-forum/${topicId}/analysis/question`)
      .pipe(
        map((result) => {
          result.searches = this.formatSearchesReferences(result.searches);
          return result;
        }),
        catchError(() => of(null))
      );
  }

  sendDiscussionTopicAiQAQuestion(projectId: string, methodId: string, topicId: string, text: string, repliedToId: string = null): Observable<AiQASearch> {
    return this.http
      .post<AiQASearch>(`${this.environment.endPoint}genie/project/${projectId}/research/${methodId}/discussion-forum/${topicId}/analysis/question`, {
        text,
        repliedToId
      })
      .pipe(
        map((result) => {
          result.answer = this.formatReference(result.answer);

          return result;
        }),
        catchError(() => of(null))
      );
  }

  private formatSearchesReferences(searches: AiQASearch[]): AiQASearch[] {
    if (!searches || searches.length === 0) {
      return [];
    }

    return searches.map((search) => {
      search.answer = this.formatReference(search.answer);
      return search;
    });
  }

  private formatReference(answer: string, pattern: RegExp = /Ref: ((\d+)(,\s*\d+)*)/g): string {
    return answer.replace(pattern, (_match, capture) => {
      const numbers = capture.split(', ').map((n) => `${n}`);
      return `Ref <sup>${numbers.join(', ')}</sup>`;
    });
  }
}
